<template>
    <div
        v-if="programItemsList && programItemsList.length"
        :class="{
            'program-row': true,
            'program-style-detail': itemStyle === 'highlight',
            'program-style-hero': itemStyle === 'hero',
            'program-style-panel': itemStyle === 'poster',
        }"
    >
        <h3 v-if="itemStyle !== 'hero'" class="program-title">{{ title }}</h3>
        <vueper-slides
            ref="slider"
            :slide-ratio="settings.slideRatio"
            :infinite="settings.infinite"
            :breakpoints="breakpoints"
            :gap="settings.gap"
            :autoplay="settings.autoplay"
            :pause-on-hover="settings.pauseOnHover"
            slide-multiple
            :visible-slides="numVisibleSlides"
            :bullets="settings.bullets"
            class="no-shadow"
            :touchable="settings.touchable"
            @next="next"
            @previous="previous"
        >
            <vueper-slide
                v-for="(item, i) in programItemsList"
                :key="i"
                :class="{
                    'mobile-slide': $mq === 'xs',
                }"
            >
                <template #content>
                    <ProgramItemDetailPoster
                        v-if="itemStyle === 'highlight'"
                        :item="item"
                        :show-date="showVideoDate"
                        :show-views="showVideoViews"
                        :subscribe-text="subscribeText"
                    ></ProgramItemDetailPoster>
                    <ProgramItemHeroPoster
                        v-else-if="itemStyle === 'hero'"
                        :item="item"
                        :show-date="showVideoDate"
                        :show-views="showVideoViews"
                        :subscribe-text="subscribeText"
                    ></ProgramItemHeroPoster>
                    <ProgramItemPoster
                        v-else
                        :item="item"
                        :show-date="showVideoDate"
                        :show-views="showVideoViews"
                        :subscribe-text="subscribeText"
                    ></ProgramItemPoster>
                </template>
            </vueper-slide>
        </vueper-slides>
    </div>
</template>

<script>
import { VueperSlides, VueperSlide } from 'vueperslides';
import ProgramItemDetailPoster from '@/components/ProgramItemDetailPoster.vue';
import ProgramItemHeroPoster from '@/components/ProgramItemHeroPoster.vue';
import ProgramItemPoster from '@/components/ProgramItemPoster.vue';
import 'vueperslides/dist/vueperslides.css';

export default {
    name: 'ProgramGroupRow',
    components: {
        VueperSlides,
        VueperSlide,
        ProgramItemDetailPoster,
        ProgramItemHeroPoster,
        ProgramItemPoster,
    },
    props: {
        groupId: {
            type: String,
            required: true,
        },
        hasUserSuggestions: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: '',
        },
        itemStyle: {
            type: String,
            default: 'poster',
        },
    },
    data: () => ({
        programItems: null,
        programItemsList: null,
        insertIndex: 0,
        showVideoDate: false,
        showVideoViews: false,
        subscribeText: 'Subscribe To Watch',
    }),
    computed: {
        settings: function () {
            switch (this.itemStyle) {
                case 'highlight':
                    return {
                        infinite: true,
                        slideRatio: 7 / 16,
                        bullets: false,
                        touchable: false,
                        autoplay: false,
                        pauseOnHover: true,
                    };
                case 'hero':
                    return {
                        infinite: true,
                        slideRatio: 657 / 1500,
                        bullets: false,
                        touchable: false,
                        autoplay: this.programItemsList.length > 1,
                        pauseOnHover: true,
                    };
                default:
                    return {
                        gap: 1,
                        infinite: false,
                        slideRatio: 0,
                        bullets: false,
                        touchable: false,
                        autoplay: false,
                        pauseOnHover: true,
                    };
            }
        },
        numVisibleSlides: function () {
            if (this.itemStyle == 'hero') {
                return 1;
            } else if (this.$mq === 'xs') {
                return 1;
            } else if (this.$mq === 'sm') {
                return 2;
            } else if (this.$mq === 'md') {
                return 3;
            } else {
                return 4;
            }
        },
        breakpoints: function () {
            switch (this.itemStyle) {
                case 'highlight':
                    return {
                        600: {
                            slideRatio: 443 / 254,
                            touchable: true,
                            infinite: true,
                            visibleSlides: 1,
                        },
                        960: {
                            slideRatio: 443 / 508,
                            visibleSlides: 2,
                        },
                        1280: {
                            slideRatio: 443 / 600,
                            visibleSlides: 3,
                        },
                    };
                case 'hero':
                    return {
                        599: {
                            slideRatio: 13 / 8,
                            touchable: true,
                            infinite: true,
                        },
                        1279: {
                            slideRatio: 5 / 8,
                        },
                        1920: {},
                    };
                default:
                    return {
                        600: {
                            touchable: true,
                            infinite: true,
                            visibleSlides: 1,
                        },
                        960: {
                            visibleSlides: 2,
                        },
                        1280: {
                            visibleSlides: 3,
                        },
                        1920: {
                            visibleSlides: 4,
                        },
                    };
            }
        },
    },
    watch: {
        groupId() {
            this.updateContents();
        },
    },
    async created() {
        const settings = await this.$appSettings;
        this.showVideoDate = !!settings.showVideoDate;
        this.showVideoViews = !!settings.showVideoViews;
        this.subscribeText = settings.hasSubscriptions
            ? settings.signupButtonText || 'Subscribe To Watch'
            : settings.signupButtonText || 'Signup To Watch';

        this.updateContents();
    },
    methods: {
        next() {
            if (this.programItems.length === 0) return;
            //add next four items to slider
            for (let i = 0; i < 4; i++) {
                this.programItemsList.splice(
                    this.insertIndex,
                    0,
                    this.programItems.shift(),
                );
                this.insertIndex++;
                if (this.programItems.length === 0) break;
            }
        },
        previous(e) {
            if (this.programItems.length === 0) return;
            //add all remaining items to slider on previous if going to the end so that animation still works
            if (e.currentSlide.index === 0) {
                for (let i = 0, l = this.programItems.length; i < l; i++) {
                    this.programItemsList.splice(
                        this.insertIndex,
                        0,
                        this.programItems.pop(),
                    );
                }

                this.$nextTick(() => {
                    this.$refs.slider &&
                        this.$refs.slider.goToSlide(
                            this.programItemsList.length - 1,
                        );
                });
            }
        },
        async updateContents() {
            const groupId = this.groupId;
            if (groupId === undefined) {
                return;
            }

            const items = this.hasUserSuggestions
                ? await this.$gideo.getSuggestedContent(groupId)
                : await this.$gideo.getProgramGroups(groupId);
            if (groupId === this.groupId) {
                //take everything except the first five
                this.programItems = items.data.slice(5);
                //take the first five
                this.programItemsList = items.data.slice(0, 5);
                this.insertIndex = 5;

                this.$nextTick(() => {
                    this.$refs.slider && this.$refs.slider.goToSlide(0);
                });
            }
        },
    },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
.program-row {
    overflow-y: visible;
    width: 100%;
    &.program-style-detail,
    &.program-style-panel {
        .vueperslides__arrow {
            opacity: 0.8;
            font-size: 1rem;
            width: 4em;
            border-radius: 50%;
            padding: 0;
            height: 4em;
            background: #eee;
            transition: opacity ease 0.3s;
            &:hover {
                opacity: 1;
            }
            svg {
                height: 4em;
                width: 4em;
                color: #333;
                padding: 1em 1em 1em 0.5em;
                box-sizing: border-box;
            }
            &.vueperslides__arrow--next {
                right: -2em;
                svg {
                    padding: 1em 0.5em 1em 1em;
                }
            }
            &.vueperslides__arrow--prev {
                left: -2em;
            }
        }

        .vueperslides__parallax-wrapper,
        .vueperslides__track,
        .vueperslides__track-inner {
            @media (min-width: 600px) {
                overflow: visible;
            }
        }
    }

    &.program-style-hero {
        .vueperslides__arrows {
            button {
                padding: 0;
            }
        }
        .vueperslides__arrow--prev {
            left: 0;
            svg {
                @media (min-width: 600px) and (max-width: 1279px) {
                    padding-left: 0.5em;
                    width: 3em;
                }
            }
        }
        .vueperslides__arrow--next {
            right: 0;
            svg {
                @media (min-width: 600px) and (max-width: 1279px) {
                    padding-right: 0.5em;
                    width: 3em;
                }
            }
        }
    }
}
.program-style-panel {
    .vueperslides__inner,
    .vueperslides__parallax-wrapper {
        padding-bottom: 0;
        height: auto;
    }
    .vueperslides__track {
        position: relative;
        height: auto;
    }
}
</style>

<style scoped lang="scss">
@import '../assets/css/_variables.scss';

.program-row {
    font-size: 1.3rem;
    @media (min-width: 600px) {
        font-size: 2rem;
    }
    @media (min-width: 1280px) {
        font-size: 1.4rem;
    }

    &:not(.program-style-hero) {
        margin-bottom: 2em;
        padding: 0em 1em;
        transition: z-index 0.35s;
        transition-delay: 0.3s;
        position: relative;
        &:hover {
            z-index: 10;
        }
        @media (min-width: 600px) {
            margin-bottom: 5em;
        }
    }

    &.program-style-hero {
        margin-bottom: 2em;
    }

    .program-title {
        margin: 1.2em 0 0.2em;
        font-size: 1.8em;
        line-height: 1.1em;
        font-weight: 800;
        text-align: left;
        letter-spacing: -0.04em;
    }
    &:not(.program-style-hero) .vueperslide {
        &:hover {
            z-index: 1;
        }
        @media (min-width: 600px) {
            visibility: hidden;
            transition: visibility 0.5s ease-in-out;
        }
        &.vueperslide--visible {
            @media (min-width: 600px) {
                visibility: visible;
            }
        }
    }
}
</style>
